/**
 * Reads the organization's demo status for a section below Pro (#2816) and
 * reports it up. A component of its own, rendered only below the plan: the
 * status is a request (and a records count) that a section under any other
 * access never asks, and a hook cannot be called conditionally in the view
 * that must keep one tree whatever the access (`VoiceOfCustomerView`).
 *
 * It reports the rule the section's requests count by too (`declinesDemo`):
 * a link or an exact window that names `includeDemo=no` makes every read 403
 * below the plan, so the demo must not open.
 *
 * A layout effect: the report lands before the first paint, so the section
 * waits on the status for no visible frame.
 */
import { useLayoutEffect } from 'react';
import { useDemoStatus, type DemoStatus } from '@/features/demo/locked-demo';
import { useStreamDemoFilter } from '../stream-filter/useStreamDemoFilter';
import type { VoiceSearch } from './voice-search';

export type VoiceDemoStatus = DemoStatus & { declinesDemo: boolean };

export function VoiceDemoStatusProbe({
  search,
  onStatus,
}: {
  search: VoiceSearch;
  onStatus: (status: VoiceDemoStatus | undefined) => void;
}) {
  const { active, loading } = useDemoStatus();
  const rule = useStreamDemoFilter(search, 'voice-section');
  const declinesDemo = rule.ready && rule.includeDemo === 'no';
  useLayoutEffect(() => {
    onStatus({ active, loading, declinesDemo });
    return () => onStatus(undefined);
  }, [active, loading, declinesDemo, onStatus]);
  return null;
}
